/** @jsxImportSource @emotion/react */
import { getCommonCodeNames } from 'apis/admin/CommonCode';
import { getBoxDetail, getBoxesByProject, getZonesByProject } from 'apis/setup/mobile/BoxLocation';
import { gertProjectsZonValidated } from 'apis/setup/mobile/MapZone';
import { findExcpByElmPrjId } from 'apis/setup/SetupSchedule';
import SearchBoxButtons from 'components/buttons/SearchBoxButtons';
import CustomInputWithSearch from 'components/inputs/CustomInputWithSearch';
import { ContainerLayout } from 'components/layouts/ContainerLayout';
import { ContentFlex } from 'components/layouts/ContentFlex';
import { SubTitleGroup, SubTitleLayout } from 'components/layouts/ContentLayout';
import { ContentSection } from 'components/layouts/ContentSection';
import {
  InputBox,
  SearchBox,
  SearchBoxRow,
  SearchButtonWrap,
  SearchCols,
  SearchRows,
} from 'components/layouts/SearchBox';
import { useLoading } from 'components/process/Loading';
import { ComboBox, MultiComboBox } from 'components/selects/ComboBox';
import CustomChip from 'components/selects/CustomChip';
import { useMessageBar } from 'hooks/useMessageBar';
import _ from 'lodash';
import { Code, commonYNList } from 'models/common/CommonCode';
import { SetupExcpParam } from 'models/setup/excp/excp';
import { Box, BoxItem, Coordinate, Zone } from 'models/setup/mobile/BoxLocation';
import { SetupProject } from 'models/setup/SetupProject';
import { SetupSchedule } from 'models/setup/SetupSchedule';
import BoxItemGrid from 'pages/setup/mobile/boxlocmanagement/BoxItemGrid';
import BoxLocationGrid from 'pages/setup/mobile/boxlocmanagement/BoxLocationGrid';
import BoxLocationMap, {
  BoxLocationMapRef,
  MarkerData,
} from 'pages/setup/mobile/boxlocmanagement/BoxLocationMap';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useSessionStore from 'stores/useSessionStore';

/**
 * @desc 하역장관리 > 하역장 Map 조회
 * @author
 * -------------------------------------------------------------------------------------
 *  Date.       author         Description
 * -------------------------------------------------------------------------------------
 * 2025.02.23   jihwan89       협력업체명 조회조건 추가
 * 2025.02.26   jihwan89       상태, 바코드관리 조회조건 추가
 */

const SetupBoxLocationManagementPage = () => {
  const { t } = useTranslation();
  const { openLoading } = useLoading();
  const { openMessageBar } = useMessageBar();

  const { userId, deptCd, deptNm, elmPrjId } = useSessionStore();
  // State - 기본 데이터
  const [boxes, setBoxes] = useState<Box[]>([]);
  const [selectedBox, setSelectedBox] = useState<Box | null>(null);
  const [items, setItems] = useState<BoxItem[]>([]);
  const [isMapActive, setIsMapActive] = useState(false);
  const [projectZones, setProjectZones] = useState<Zone[]>([]);
  const [unloadZones, setUnloadZones] = useState<Zone[]>([]);
  const [filteredBoxes, setFilteredBoxes] = useState<Box[]>([]);
  const [prjExcpData, setPrjExcpData] = useState<SetupSchedule[]>([]); //협력사

  // State - 코드 데이터
  const [bildPlntCode, setBildPlntCode] = useState<Code[]>([]);
  const [boxStausCode, setBoxStatusCode] = useState<Code[]>([]);
  const [prjMasterData, setPrjMasterData] = useState<SetupProject[]>([]);

  // State - 통합 검색 조건 (6개)
  const defaultSearchCondition = {
    bildPlntCds: [] as string[],
    elmPrjId: '',
    bmkYn: 'A', // 즐겨찾기: 'A' 전체, 'Y' 즐겨찾기
    boxNo: '',
    keyword: '', // 설비ID/설비명/품목명 통합 (OR 검색)
    zonCd: '', // 구역명 필터
    excpId: '', // 협력업체
    bcdYn: '', // 바코드발행여부
    boxStatCd: [] as string[],
    boxStatCds: [] as string[],
  };
  const [searchCondition, setSearchCondition] = useState(defaultSearchCondition);

  const isEmailFormat = () => {
    if (!userId) return false;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(userId);
  };

  const initSearchParam = {
    bildPlntCd: [],
    elmPrjId: '',
    excpId: '',
    searchOwnedPrjYn: isEmailFormat() ? 'Y' : 'N',
  };

  const [excpParam, setExcpParam] = useState<SetupExcpParam>(initSearchParam);

  // State - 지도 데이터
  const [mapData, setMapData] = useState<{
    areaCoordinates: Coordinate[];
    zones: Zone[];
    markers: MarkerData[];
  } | null>(null);

  // State - 지도 fitBounds 제어
  const [fitBoundsTarget, setFitBoundsTarget] = useState<'area' | 'zone' | 'none'>('none');
  const [zoneBounds, setZoneBounds] = useState<Coordinate[] | undefined>(undefined);
  const lastSearchedPrjIdRef = useRef<string>(''); // 마지막 조회한 프로젝트 ID

  // BoxLocationMap ref (구역 이동용)
  const mapRef = useRef<BoxLocationMapRef | null>(null);

  useEffect(() => {
    initCodes();
  }, []);

  const initCodes = async () => {
    const bildPlntCodeData = await getCommonCodeNames('FA_SU_PLNT_CD');
    setBildPlntCode(bildPlntCodeData);
    const boxStatusCodeData = await getCommonCodeNames('BOX_STATUS');
    setBoxStatusCode(boxStatusCodeData);
    // 기존: findProject({ searchScheduleYn: 'Y' })
    // 변경: USE_YN = 'Y' & 좌표가 존재하는 하역장 조회 & scheduleYn 조건 포함
    gertProjectsZonValidated().then((result: SetupProject[]) => {
      if (result !== null) {
        setPrjMasterData(result);
      } else {
        openMessageBar({
          type: 'error',
          content: t(
            'com.msg.요청 정보 조회 중 오류가 발생했습니다.',
            '요청 정보 조회 중 오류가 발생했습니다.'
          ),
        });
      }
    });
  };

  const optionsPrjMaster = useMemo(() => {
    return (prjMasterData || [])
      .filter((o) =>
        (searchCondition?.bildPlntCds || []).length > 0
          ? searchCondition.bildPlntCds.includes(o.bildPlntCd || '')
          : true
      )
      .reduce(
        (acc, cur) => [
          ...acc,
          {
            cmnCd: cur.elmPrjId,
            cmnCdNm: cur.elmPrjNm,
          } as Code,
        ],
        [] as Code[]
      );
  }, [prjMasterData, searchCondition?.bildPlntCds]);

  const optionsPrjExcp = useMemo(() => {
    const result = Array.from(
      new Map(
        (prjExcpData || []).map((cur) => [
          `${cur.excpId}_${cur.excpNm}`,
          { cmnCd: cur?.excpId || '', cmnCdNm: cur?.excpNm || '' },
        ])
      ).values()
    );
    return result;
  }, [prjExcpData]);

  useEffect(() => {
    if (searchCondition?.elmPrjId) {
      findExcpByElmPrjId(searchCondition?.elmPrjId).then((result: SetupSchedule[]) => {
        setPrjExcpData(result);
      });
    } else {
      setPrjExcpData([]);
    }
  }, [searchCondition?.elmPrjId]);

  const handleConditionChange = (name: string, value: any) => {
    setSearchCondition((prev) => ({ ...prev, [name]: value }));

    // 플랜트 변경 시 프로젝트 선택 초기화 (해당 플랜트에 없는 프로젝트인 경우)
    if (name === 'bildPlntCds') {
      const selectedProject = prjMasterData.find((p) => p.elmPrjId === searchCondition.elmPrjId);
      if (selectedProject && value.length > 0 && !value.includes(selectedProject.bildPlntCd)) {
        setSearchCondition((prev) => ({ ...prev, elmPrjId: '' }));
      }
    }
  };

  /**
   * 통합 검색 로직
   * - 모든 조건 클라이언트 필터링 (boxNo, zonCd, keyword)
   * - keyword: 품목목록명(pakBoxNm), 설비ID(eqpIds), 설비명(eqpNms) OR 검색
   */
  const handleUnifiedSearch = async () => {
    const { elmPrjId, boxNo, keyword, zonCd, bmkYn, excpId, boxStatCd, bcdYn } = searchCondition;

    // 프로젝트 필수 체크
    if (!elmPrjId) {
      openMessageBar({
        type: 'error',
        content: t(
          'com.label.프로젝트를 선택한 후 조회해주세요.',
          '프로젝트를 선택한 후 조회해주세요.'
        ),
      });
      return;
    }

    openLoading(true);
    try {
      // 조건 분류
      const hasFilterCondition = !!(boxNo || zonCd || keyword || excpId || boxStatCd || bcdYn);
      // 즐겨찾기 필터 (Y일 때만 BMK_Y 전달)
      const bmkStatus = bmkYn === 'Y' ? 'BMK_Y' : undefined;

      // 1. API 병렬 호출: 박스 목록 + 구역 목록
      const [boxListData, zoneListData] = await Promise.all([
        getBoxesByProject(elmPrjId, '', bmkStatus),
        getZonesByProject(elmPrjId),
      ]);

      if (!boxListData || !Array.isArray(boxListData)) {
        setBoxes([]);
        setFilteredBoxes([]);
        setIsMapActive(false);
        setMapData(null);
        return;
      }

      // Zone 데이터 처리
      const firstProjectZone = (zoneListData || []).find((z) => z.prjZonYn === 'Y');
      const projectZonesData = firstProjectZone ? [firstProjectZone] : [];
      const unloadZonesData = (zoneListData || []).filter((z) => z.prjZonYn !== 'Y');

      setProjectZones(projectZonesData);
      setUnloadZones(unloadZonesData);

      // 박스에 구역명(zonNm) 매핑
      const boxesWithZoneName = boxListData.map((box) => {
        if (box.unloZoneCd) {
          const zone = unloadZonesData.find((z) => z.mapZonId === box.unloZoneCd);
          return { ...box, zonNm: zone?.zonNm || '' };
        }
        return { ...box, zonNm: '' };
      });

      // 전체 박스 저장
      setBoxes(boxesWithZoneName);
      setIsMapActive(true);

      // 3. 필터링 로직 (클라이언트)
      let resultBoxes = [...boxesWithZoneName];

      if (hasFilterCondition) {
        resultBoxes = resultBoxes.filter((box) => {
          // 박스No 필터
          const matchBoxNo = !boxNo || box.boxNo?.toLowerCase().includes(boxNo.toLowerCase());
          // 구역 필터
          const matchZonCd = !zonCd || box.unloZoneCd === zonCd;
          // 설비ID/설비명/품목명 필터 (OR 조건)
          const matchKeyword =
            !keyword ||
            box.pakBoxNm?.toLowerCase().includes(keyword.toLowerCase()) ||
            box.eqpIds?.toLowerCase().includes(keyword.toLowerCase()) ||
            box.eqpNms?.toLowerCase().includes(keyword.toLowerCase());
          // 협력업체
          const matchExcpId = !excpId || box.excpId === excpId;
          // 상태
          const matchBoxStatus = _.isEmpty(boxStatCd)
            ? true
            : false || boxStatCd?.includes(box.boxStatCd || '');
          // 바코드관리
          const matchBcdYn = !bcdYn || box.bcdYn === bcdYn;

          return (
            matchBoxNo && matchZonCd && matchKeyword && matchExcpId && matchBoxStatus && matchBcdYn
          );
        });
      }

      setFilteredBoxes(resultBoxes);

      // 4. 첫 번째 박스 자동 선택 및 품목 조회
      if (resultBoxes.length > 0) {
        const firstBox = resultBoxes[0];
        try {
          const detailBox = await getBoxDetail(firstBox.boxId!);
          if (detailBox) {
            setSelectedBox(detailBox);
            setItems(detailBox.items || []);
          } else {
            setSelectedBox(firstBox);
            setItems(firstBox.items || []);
          }
        } catch (error) {
          setSelectedBox(firstBox);
          setItems(firstBox.items || []);
        }
      } else {
        setSelectedBox(null);
        setItems([]);
        if (hasFilterCondition) {
          openMessageBar({
            type: 'warning',
            content: t('com.msg.검색 결과가 없습니다.', '검색 결과가 없습니다.'),
          });
        }
      }

      // 5. 지도 데이터 생성
      const mapDataResult = transformBoxesToMapData(
        resultBoxes,
        projectZonesData,
        unloadZonesData,
        resultBoxes.length > 0 ? resultBoxes[0] : null
      );
      setMapData(mapDataResult);

      // 6. fitBounds 설정
      const isProjectChanged = lastSearchedPrjIdRef.current !== elmPrjId;

      if (zonCd) {
        // 구역 선택 시 해당 구역으로 확대
        const selectedZone = unloadZonesData.find((z) => z.mapZonId === zonCd);
        if (selectedZone?.coordinates) {
          setZoneBounds(selectedZone.coordinates);
          setFitBoundsTarget('zone');
        }
      } else if (isProjectChanged) {
        // 프로젝트 변경 시 대표구역으로
        setFitBoundsTarget('area');
      } else {
        // 같은 프로젝트 재조회 시 지도 상태 유지
        setFitBoundsTarget('none');
      }

      // 마지막 조회 프로젝트 ID 저장
      lastSearchedPrjIdRef.current = elmPrjId;
    } catch (error) {
      console.error('검색 실패:', error);
      setBoxes([]);
      setFilteredBoxes([]);
      setIsMapActive(false);
      setMapData(null);
      openMessageBar({
        type: 'error',
        content: t('com.msg.검색 중 오류가 발생했습니다.', '검색 중 오류가 발생했습니다.'),
      });
    } finally {
      openLoading(false);
    }
  };

  // 초기화 핸들러
  const handleReset = () => {
    setSearchCondition(defaultSearchCondition);
    setBoxes([]);
    setFilteredBoxes([]);
    setSelectedBox(null);
    setItems([]);
    setIsMapActive(false);
    setMapData(null);
    setFitBoundsTarget('none');
    setZoneBounds(undefined);
    lastSearchedPrjIdRef.current = ''; // 마지막 조회 프로젝트 ID 리셋
  };

  // 박스 row 선택 시 (마커 선택 + 품목 표시)
  const handleBoxSelect = async (box: Box | null) => {
    setSelectedBox(box);
    if (!box) {
      setItems([]);
      return;
    }
    setItems(box.items || []);
  };

  // 지도 마커 클릭 시 (그리드 row 선택 + 품목 조회)
  const handleMarkerClick = async (markerId: string) => {
    const box = boxes.find((b) => b.boxId === markerId);
    if (box && box.boxId) {
      try {
        const detailBox = await getBoxDetail(box.boxId); // API 호출
        if (detailBox) {
          setSelectedBox(detailBox);
          setItems(detailBox.items || []); // items 조회됨
        } else {
          setSelectedBox(box);
          setItems([]);
        }
      } catch (error) {
        console.error('박스 상세 조회 실패:', error);
        setSelectedBox(box);
        setItems([]);
      }
    }
  };

  // selectedBox 또는 filteredBoxes 변경 시 mapData 재생성 (마커 선택 상태 업데이트)
  useEffect(() => {
    if (filteredBoxes.length > 0) {
      const updatedMapData = transformBoxesToMapData(
        filteredBoxes,
        projectZones,
        unloadZones,
        selectedBox
      );
      setMapData(updatedMapData);
    }
  }, [selectedBox, filteredBoxes]);

  // --- 품목 선택 시 해당 박스로 이동 ---
  const handleItemSelect = async (item: BoxItem) => {
    if (!item.boxId) return;

    // filteredBoxes에서 해당 박스 찾기
    const targetBox = filteredBoxes.find((b) => b.boxId === item.boxId);
    if (targetBox) {
      try {
        const detailBox = await getBoxDetail(targetBox.boxId!);
        if (detailBox) {
          setSelectedBox(detailBox);
          setItems(detailBox.items || []);
        } else {
          setSelectedBox(targetBox);
          setItems(targetBox.items || []);
        }
      } catch (error) {
        setSelectedBox(targetBox);
        setItems(targetBox.items || []);
      }
    } else {
      openMessageBar({
        type: 'warning',
        content: t('com.msg.해당 박스를 찾을 수 없습니다.', '해당 박스를 찾을 수 없습니다.'),
      });
    }
  };

  /**
   * 폴리곤 내부의 랜덤 좌표 생성 (바운딩 박스 기반)
   */
  const getRandomPointInPolygon = (
    coordinates: Coordinate[]
  ): { lat: number; lng: number } | null => {
    if (!coordinates || coordinates.length === 0) return null;

    const lats = coordinates.map((c) => parseFloat(c.xcrd || '0'));
    const lngs = coordinates.map((c) => parseFloat(c.ycrd || '0'));
    const minLat = Math.min(...lats);
    const maxLat = Math.max(...lats);
    const minLng = Math.min(...lngs);
    const maxLng = Math.max(...lngs);

    return {
      lat: minLat + Math.random() * (maxLat - minLat),
      lng: minLng + Math.random() * (maxLng - minLng),
    };
  };

  /**
   * Box 배열과 Zone 배열을 지도 데이터로 변환
   * - areaCoordinates: 대표구역(projectZones)의 모든 좌표 평탄화
   * - zones: 하역구역(unloadZones) 그대로 전달
   * - markers: Box 목록의 xcrd, ycrd로 마커 생성
   *   - FABIN_DONE 상태 박스는 마커 제외
   *   - 좌표 없는 박스는 unloZoneCd 구역 내 랜덤 위치에 마커 생성
   */
  const transformBoxesToMapData = (
    boxes: Box[],
    projectZones: Zone[],
    unloadZones: Zone[],
    selectedBox: Box | null
  ) => {
    // 영역 좌표 (대표구역의 모든 좌표를 평탄화)
    const areaCoordinates = projectZones.flatMap((zone) => zone.coordinates || []);

    // Box 목록으로 마커 생성 (FABIN_DONE 제외)
    const markers: MarkerData[] = boxes
      .filter((box) => box.boxStatCd !== 'FABIN_DONE') // FABIN_DONE 제외
      .filter((box) => (box.xcrd && box.ycrd) || box.unloZoneCd) // 좌표 있거나 구역코드 있는 박스만
      .map((box, index) => {
        let lat: number;
        let lng: number;

        if (box.xcrd && box.ycrd) {
          // 좌표가 있으면 그대로 사용
          lat = parseFloat(box.xcrd);
          lng = parseFloat(box.ycrd);
        } else if (box.unloZoneCd) {
          // 좌표 없으면 해당 구역 내 랜덤 위치
          const zone = unloadZones.find((z) => z.mapZonId === box.unloZoneCd);
          if (zone?.coordinates?.length) {
            const randomPos = getRandomPointInPolygon(zone.coordinates);
            if (randomPos) {
              lat = randomPos.lat;
              lng = randomPos.lng;
            } else {
              return null; // 좌표 생성 실패
            }
          } else {
            return null; // 구역 좌표 없음
          }
        } else {
          return null;
        }

        const isSelected = box.boxId === selectedBox?.boxId;
        return {
          id: box.boxId || `marker-${index}`,
          name: `${box.pakBoxNm || 'Box'} (${box.boxStatCdNm || ''})`,
          position: { lat, lng },
          selected: isSelected,
        };
      })
      .filter((marker): marker is MarkerData => marker !== null); // null 제거

    return {
      areaCoordinates,
      zones: unloadZones, // 하역구역을 그대로 전달 (BoxLocationMap에서 변환)
      markers,
    };
  };

  return (
    <ContainerLayout
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: 'calc(100vh - 180px)',
        overflow: 'hidden',
      }}
    >
      {/* 통합 검색 영역 - 5개 필드 (2행: 2열 + 3열) */}
      <SearchBox>
        <SearchBoxRow>
          <InputBox>
            {/* 1행: 플랜트 / 프로젝트명 / 즐겨찾기 */}
            <SearchRows>
              <SearchCols className="width25">
                <label>{String(t('setup.label.플랜트', '플랜트'))}</label>
                <MultiComboBox
                  id="bildPlntCd"
                  options={bildPlntCode}
                  placeholder={String(t('com.label.전체', '전체'))}
                  defaultValue={searchCondition.bildPlntCds}
                  onChange={(value) => handleConditionChange('bildPlntCds', value)}
                />
              </SearchCols>
              <SearchCols className="width3">
                <label className="required" style={{ minWidth: '180px' }}>
                  {String(t('setup.label.프로젝트명', '프로젝트명'))}
                </label>
                <ComboBox
                  placeholder={String(
                    t('com.label.프로젝트를 선택해주세요', '프로젝트를 선택해주세요')
                  )}
                  options={optionsPrjMaster}
                  defaultValue={searchCondition.elmPrjId}
                  onChange={(value) => handleConditionChange('elmPrjId', value)}
                />
              </SearchCols>
              <SearchCols className="width25">
                <label>{String(t('setup.label.즐겨찾기', '즐겨찾기'))}</label>
                <CustomChip
                  options={
                    [
                      { cmnCd: 'A', cmnCdNm: String(t('com.label.전체', '전체')) },
                      { cmnCd: 'Y', cmnCdNm: String(t('com.label.즐겨찾기', '즐겨찾기')) },
                    ] as Code[]
                  }
                  defaultValue={searchCondition.bmkYn}
                  onChange={(value) => {
                    if (value === '') {
                      handleConditionChange('bmkYn', 'A');
                    } else {
                      handleConditionChange('bmkYn', value.toString());
                    }
                  }}
                  singleSelect={true}
                />
              </SearchCols>
            </SearchRows>
            {/* 2행: 박스No / 설비ID/설비명/품목명 / 구역명 */}
            <SearchRows>
              <SearchCols className="width25">
                <label>{String(t('setup.label.박스No', '박스No'))}</label>
                <CustomInputWithSearch
                  value={searchCondition.boxNo}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    handleConditionChange('boxNo', e.target.value)
                  }
                  placeholder={String(t('setup.msg.박스No를 입력하세요', '박스No를 입력하세요'))}
                />
              </SearchCols>
              <SearchCols className="width3">
                <label style={{ minWidth: '180px' }}>
                  {String(t('setup.label.M-CODE/설비명/품목명', 'M-CODE/설비명/품목명'))}
                </label>
                <CustomInputWithSearch
                  value={searchCondition.keyword}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    handleConditionChange('keyword', e.target.value)
                  }
                  placeholder={String(
                    t(
                      'setup.msg.M-CODE, 설비명 또는 품목명을 입력하세요',
                      'M-CODE, 설비명 또는 품목명을 입력하세요'
                    )
                  )}
                />
              </SearchCols>
              <SearchCols className="width25">
                <label>{String(t('setup.grid.협력업체명', '협력업체명'))}</label>
                <ComboBox
                  placeholder={String(t('com.label.전체', '전체'))}
                  options={optionsPrjExcp}
                  defaultValue={searchCondition?.excpId}
                  onChange={(value) => {
                    handleConditionChange('excpId', value);
                  }}
                />
              </SearchCols>
            </SearchRows>
            <SearchRows>
              <SearchCols className="width25">
                <label>{String(t('setup.grid.상태', '상태'))}</label>
                <MultiComboBox
                  id="boxStatCd"
                  options={boxStausCode}
                  placeholder={String(t('com.label.전체', '전체'))}
                  defaultValue={searchCondition?.boxStatCds}
                  onChange={(value) => {
                    console.log('value', value);
                    setSearchCondition({ ...searchCondition, boxStatCd: value });
                  }}
                />
              </SearchCols>
              <SearchCols className="width3">
                <label style={{ minWidth: '180px' }}>
                  {String(t('setup.grid.구역명', '구역명'))}
                </label>
                <ComboBox
                  placeholder={String(t('com.label.전체', '전체'))}
                  options={unloadZones.map(
                    (zone) => ({ cmnCd: zone.mapZonId, cmnCdNm: zone.zonNm } as Code)
                  )}
                  defaultValue={searchCondition.zonCd}
                  onChange={(value) => handleConditionChange('zonCd', value)}
                />
              </SearchCols>
              <SearchCols className="width25">
                <label>{t('com.label.바코드 발행여부', '바코드 발행여부')}</label>
                <ComboBox
                  placeholder={String(t('com.label.선택', '선택'))}
                  options={commonYNList}
                  defaultValue={searchCondition?.bcdYn}
                  onChange={(value) => {
                    handleConditionChange('bcdYn', value);
                  }}
                />
              </SearchCols>
            </SearchRows>
          </InputBox>
          <SearchButtonWrap>
            <SearchBoxButtons
              defaultCondition={defaultSearchCondition}
              setReset={handleReset}
              onSearch={handleUnifiedSearch}
            />
          </SearchButtonWrap>
        </SearchBoxRow>
      </SearchBox>

      {/* 메인 컨텐츠 영역 */}
      <ContentFlex style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
        {/* 왼쪽 - 그리드 영역 */}
        <div
          className="section width50p"
          style={{ display: 'flex', flexDirection: 'column', gap: '8px', minHeight: 0 }}
        >
          {/* 박스 목록 */}
          <ContentSection
            className="marginT0"
            style={{ flex: 3, display: 'flex', flexDirection: 'column', minHeight: 0 }}
          >
            <BoxLocationGrid
              boxes={filteredBoxes}
              onBoxSelect={handleBoxSelect}
              selectedBoxId={selectedBox?.boxId}
            />
          </ContentSection>

          {/* 품목 목록 */}
          <ContentSection
            className="marginT0"
            style={{ flex: 2, display: 'flex', flexDirection: 'column', minHeight: 0 }}
          >
            <BoxItemGrid
              items={items}
              boxName={selectedBox?.pakBoxNm}
              onItemSelect={handleItemSelect}
            />
          </ContentSection>
        </div>

        {/* 오른쪽 - 지도 영역 */}
        <ContentSection
          className="section width50p marginT0"
          style={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}
        >
          <SubTitleLayout>
            <SubTitleGroup>
              <h3>{t('setup.label.박스 위치', '박스 위치')}</h3>
            </SubTitleGroup>
          </SubTitleLayout>
          <div style={{ flex: 1, minHeight: 0, paddingBottom: '13.5px' }}>
            <BoxLocationMap
              ref={mapRef}
              areaCoordinates={mapData?.areaCoordinates}
              zones={mapData?.zones}
              markers={mapData?.markers}
              onMarkerClick={handleMarkerClick}
              fitBoundsTarget={fitBoundsTarget}
              zoneBounds={zoneBounds}
              onFitBoundsComplete={() => setFitBoundsTarget('none')}
            />
          </div>
        </ContentSection>
      </ContentFlex>
    </ContainerLayout>
  );
};

export default SetupBoxLocationManagementPage;
